'use client';
import React, { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import { entryService, userService } from '@/lib/supabaseService';
import { CLIMBS, type ClimbEntry, type UserProfile } from '@/lib/climbData';
import Icon from '@/components/ui/AppIcon';

type SearchMode = 'riders' | 'climbs';
type DifficultyFilter = 'all' | 'hard' | 'extreme';

function avatarColors(initials: string): string {
  const palette = [
    'bg-orange-500/20 text-orange-400 border-orange-500/30',
    'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    'bg-red-500/20 text-red-400 border-red-500/30',
    'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'bg-green-500/20 text-green-400 border-green-500/30',
    'bg-pink-500/20 text-pink-400 border-pink-500/30',
    'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  ];
  const idx = (initials.charCodeAt(0) + (initials.charCodeAt(1) || 0)) % palette.length;
  return palette[idx];
}

function formatSeconds(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

const DIFFICULTY_LABEL: Record<string, string> = { medium: 'Media', hard: 'Difficile', extreme: 'Estrema' };
const DIFFICULTY_COLOR: Record<string, string> = {
  medium: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
  hard: 'text-orange-400 bg-orange-500/10 border-orange-500/30',
  extreme: 'text-red-400 bg-red-500/10 border-red-500/30',
};

export default function SearchPage() {
  const [mode, setMode] = useState<SearchMode>('riders');
  const [query, setQuery] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState<DifficultyFilter>('all');
  const [minElevation, setMinElevation] = useState(0);
  const [maxGradientFilter, setMaxGradientFilter] = useState(0);
  const [allEntries, setAllEntries] = useState<ClimbEntry[]>([]);
  const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [entries, users] = await Promise.all([entryService.getAll(), userService.getAll()]);
        setAllEntries(entries);
        setAllUsers(users);
      } catch {
        setAllEntries([]);
        setAllUsers([]);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const approvedEntries = allEntries.filter((e) => e.status === 'approved');
  const activeUsers = allUsers.filter((u) => u.status === 'active' && u.role !== 'admin');

  const totalRiders = activeUsers.length;
  const totalEntries = approvedEntries.length;
  const avgWkg = approvedEntries.length > 0
    ? (approvedEntries.reduce((s, e) => s + e.wattPerKg, 0) / approvedEntries.length).toFixed(1)
    : '0.0';
  const topRider = activeUsers.find((u) => {
    const userEntries = approvedEntries.filter((e) => e.userId === u.id);
    return new Set(userEntries.map((e) => e.climbId)).size === 9;
  });

  const filteredRiders = useMemo(() => {
    if (!query.trim()) return activeUsers;
    const q = query.toLowerCase();
    return activeUsers.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  }, [query, activeUsers]);

  const filteredClimbs = useMemo(() => {
    let list = CLIMBS;
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(q) || c.location.toLowerCase().includes(q));
    }
    if (difficultyFilter !== 'all') list = list.filter((c) => c.difficulty === difficultyFilter);
    if (minElevation > 0) list = list.filter((c) => c.elevation >= minElevation);
    if (maxGradientFilter > 0) list = list.filter((c) => c.avgGradient >= maxGradientFilter);
    return list;
  }, [query, difficultyFilter, minElevation, maxGradientFilter]);

  function getRiderStats(userId: string) {
    const entries = approvedEntries.filter((e) => e.userId === userId);
    const climbsCompleted = new Set(entries.map((e) => e.climbId)).size;
    const bestWkg = entries.length > 0 ? Math.max(...entries.map((e) => e.wattPerKg)) : 0;
    return { climbsCompleted, bestWkg };
  }

  function getClimbStats(climbId: string) {
    const entries = approvedEntries.filter((e) => e.climbId === climbId);
    const leader = entries.reduce((best, e) => (!best || e.timeSeconds < best.timeSeconds ? e : best), null as ClimbEntry | null);
    return { count: entries.length, leader };
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-sans font-extrabold text-3xl text-foreground mb-1">
            <span className="text-gradient-orange">Cerca</span> nel Club
          </h1>
          <p className="text-muted-foreground text-sm">
            Trova ciclisti, esplora salite e scopri statistiche rapide del club IA Climb.
          </p>
        </div>

        {/* Quick Stats Bento */}
        {loading ? (
          <div className="flex items-center justify-center py-8 mb-8">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <div className="bg-card border border-border rounded-xl p-4 flex flex-col gap-1">
              <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Ciclisti Attivi</span>
              <span className="text-2xl font-extrabold text-foreground font-mono">{totalRiders}</span>
              <span className="text-xs text-green-400 flex items-center gap-1"><Icon name="UserGroupIcon" size={12} /> Membri verificati</span>
            </div>
            <div className="bg-card border border-border rounded-xl p-4 flex flex-col gap-1">
              <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Tempi Approvati</span>
              <span className="text-2xl font-extrabold text-foreground font-mono">{totalEntries}</span>
              <span className="text-xs text-orange-400 flex items-center gap-1"><Icon name="CheckBadgeIcon" size={12} /> Su 9 salite</span>
            </div>
            <div className="bg-card border border-border rounded-xl p-4 flex flex-col gap-1">
              <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Media W/kg</span>
              <span className="text-2xl font-extrabold text-foreground font-mono">{avgWkg}</span>
              <span className="text-xs text-yellow-400 flex items-center gap-1"><Icon name="BoltIcon" size={12} /> Potenza media club</span>
            </div>
            <div className="bg-card border border-border rounded-xl p-4 flex flex-col gap-1">
              <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Top Completo</span>
              <span className="text-lg font-extrabold text-foreground truncate">{topRider ? topRider.name.split(' ')[0] : '—'}</span>
              <span className="text-xs text-purple-400 flex items-center gap-1"><Icon name="TrophyIcon" size={12} /> 9/9 salite</span>
            </div>
          </div>
        )}

        {/* Mode Tabs + Search Bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex bg-card border border-border rounded-xl p-1 gap-1 shrink-0">
            <button onClick={() => { setMode('riders'); setQuery(''); }} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${mode === 'riders' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}>
              <Icon name="UserGroupIcon" size={15} />Ciclisti
            </button>
            <button onClick={() => { setMode('climbs'); setQuery(''); }} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${mode === 'climbs' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}>
              <Icon name="MapIcon" size={15} />Salite
            </button>
          </div>
          <div className="relative flex-1">
            <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={mode === 'riders' ? 'Cerca per nome o email…' : 'Cerca per nome salita o regione…'} className="w-full pl-9 pr-4 py-2.5 bg-card border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition" />
            {query && (
              <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <Icon name="XMarkIcon" size={15} />
              </button>
            )}
          </div>
        </div>

        {/* Climb Filters */}
        {mode === 'climbs' && (
          <div className="flex flex-wrap gap-3 mb-6 p-4 bg-card border border-border rounded-xl">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground font-mono">Difficoltà:</span>
              {(['all', 'hard', 'extreme'] as DifficultyFilter[]).map((d) => (
                <button key={d} onClick={() => setDifficultyFilter(d)} className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${difficultyFilter === d ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted border-border text-muted-foreground hover:text-foreground'}`}>
                  {d === 'all' ? 'Tutte' : d === 'hard' ? 'Difficile' : 'Estrema'}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground font-mono">Dislivello min:</span>
              {[0, 800, 1200, 1500].map((v) => (
                <button key={v} onClick={() => setMinElevation(v)} className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${minElevation === v ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted border-border text-muted-foreground hover:text-foreground'}`}>
                  {v === 0 ? 'Tutti' : `${v}m+`}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground font-mono">Pendenza media:</span>
              {[0, 8, 10].map((v) => (
                <button key={v} onClick={() => setMaxGradientFilter(v)} className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${maxGradientFilter === v ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted border-border text-muted-foreground hover:text-foreground'}`}>
                  {v === 0 ? 'Tutte' : `≥${v}%`}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : mode === 'riders' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredRiders.length === 0 ? (
              <div className="col-span-full text-center py-16">
                <Icon name="UserGroupIcon" size={40} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Nessun ciclista trovato</p>
              </div>
            ) : filteredRiders.map((user) => {
              const stats = getRiderStats(user.id);
              const colorClass = avatarColors(user.avatar);
              return (
                <Link key={`rider-${user.id}`} href={`/member/${user.id}`} className="bg-card border border-border rounded-xl p-4 hover:border-primary/40 hover:card-glow transition-all duration-200 group">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-full border flex items-center justify-center text-sm font-bold flex-shrink-0 ${colorClass}`}>
                      {user.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground text-sm truncate group-hover:text-primary transition-colors">{user.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-muted/50 rounded-lg px-2.5 py-2">
                      <p className="text-xs text-muted-foreground">Salite</p>
                      <p className="text-sm font-bold font-mono-nums text-foreground">{stats.climbsCompleted}/9</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg px-2.5 py-2">
                      <p className="text-xs text-muted-foreground">Best W/kg</p>
                      <p className="text-sm font-bold font-mono-nums text-foreground">{stats.bestWkg > 0 ? stats.bestWkg.toFixed(1) : '—'}</p>
                    </div>
                  </div>
                  {user.clubVerified && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-green-400 font-semibold">
                      <Icon name="CheckBadgeIcon" size={12} />Verificato IA Climb
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClimbs.length === 0 ? (
              <div className="col-span-full text-center py-16">
                <Icon name="MapIcon" size={40} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Nessuna salita trovata</p>
              </div>
            ) : filteredClimbs.map((climb) => {
              const stats = getClimbStats(climb.id);
              return (
                <div key={`climb-${climb.id}`} className="bg-card border border-border rounded-xl p-4 hover:border-primary/40 hover:card-glow transition-all duration-200">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold text-foreground text-sm">{climb.name}</p>
                      <p className="text-xs text-muted-foreground">{climb.location}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-md border ${DIFFICULTY_COLOR[climb.difficulty] ?? ''}`}>
                      {DIFFICULTY_LABEL[climb.difficulty] ?? climb.difficulty}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {[
                      { label: 'Distanza', value: `${climb.distance} km` },
                      { label: 'Dislivello', value: `+${climb.elevation}m` },
                      { label: 'Pend. media', value: `${climb.avgGradient}%` },
                      { label: 'Partecipanti', value: stats.count.toString() },
                    ].map((s) => (
                      <div key={s.label} className="bg-muted/50 rounded-lg px-2.5 py-2">
                        <p className="text-xs text-muted-foreground">{s.label}</p>
                        <p className="text-sm font-bold font-mono-nums text-foreground">{s.value}</p>
                      </div>
                    ))}
                  </div>
                  {stats.leader && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground border-t border-border pt-2">
                      <Icon name="TrophyIcon" size={12} className="text-yellow-400" />
                      <span className="font-semibold text-foreground">{stats.leader.userName}</span>
                      <span className="font-mono-nums text-accent">{stats.leader.timeFormatted}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
